import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {DropdownSelect} from 'components/common/dropdown/DropdownSelect.component';
import {DropdownPosition, DropdownSelectTypes} from 'types/gaiaComponents/dropdownButton.types';
import {IDropdownListItem} from 'types/gaiaComponents/dropdownButton';
import * as Api from 'utils/api/api';
import {LOADING_STATUS} from 'utils/constants';
import './ContextSelector.component.scss';

type UserContext = {
    id: number;
    name: string;
};

type LoadingStatus = (typeof LOADING_STATUS)[keyof typeof LOADING_STATUS];

type Props = {
    currentContextId: number;
    companyName?: string;
    translate: (key: string) => string;
    onSelection: (contextId: number) => void;
};

// the selector is slotted into the navigation, so its dropdown cannot escape the stacking
// context the navigation puts it in - page content that raises itself above the top bar drops
// out of the way instead (global.scss)
export const CONTEXT_SELECTOR_OPEN_CLASS = 'context-selector-open';

const ContextSelector: React.FC<Props> = ({currentContextId, companyName, translate, onSelection}) => {
    const [contexts, setContexts] = useState<UserContext[]>([]);
    const [status, setStatus] = useState<LoadingStatus>(LOADING_STATUS.LOADING);

    const fetchContexts = useCallback(() => {
        setStatus(LOADING_STATUS.LOADING);
        Api.getUserContexts().then(
            (result: UserContext[]) => {
                setContexts(result);
                setStatus(LOADING_STATUS.DONE);
            },
            () => {
                setStatus(LOADING_STATUS.ERROR);
            },
        );
    }, []);

    useEffect(() => {
        fetchContexts();
    }, [fetchContexts]);

    const isEmpty = status !== LOADING_STATUS.DONE || contexts.length === 0;

    const onOpenChange = useCallback((open: boolean) => {
        // an empty selector shows no list, so nothing has to move out of its way
        document.body.classList.toggle(CONTEXT_SELECTOR_OPEN_CLASS, open && !isEmpty);
    }, [isEmpty]);

    useEffect(() => () => document.body.classList.remove(CONTEXT_SELECTOR_OPEN_CLASS), []);

    const items = useMemo<IDropdownListItem[]>(() =>
        contexts.map(ctx => ({
            key: String(ctx.id),
            text: ctx.name,
            title: ctx.name,
            selected: ctx.id === currentContextId,
            onClick: () => onSelection(ctx.id),
        })), [contexts, currentContextId, onSelection]);

    const value = companyName || translate('menu.userBox.contextPlaceholder');

    return (
        <DropdownSelect
            id="context-selector"
            type={DropdownSelectTypes.VALUE}
            value={value}
            items={items}
            position={DropdownPosition.BOTTOM_RIGHT}
            onOpenChange={onOpenChange}
            isEmpty={isEmpty}
            extraClasses="context-selector"
            isSearchable
            searchPlaceholder={translate('menu.userBox.contextSearchPlaceholder')}
            searchAriaLabel={translate('menu.userBox.contextSearchAriaLabel')}
            noMatchText={translate('menu.userBox.contextNoMatch')}
        />
    );
};

export default ContextSelector;
